import { css } from '@emotion/react'
import { Launcher, theme as launcherTheme } from '@kmns-internal/app-launcher'
import {
  AnalyticsOutlined,
  AssignmentOutlined,
  BarChartOutlined,
  CalendarMonthOutlined,
  DatasetOutlined,
  EventRepeatOutlined,
  Logout,
  MenuOutlined,
  PersonOutline,
  SettingsOutlined,
  SpeedOutlined,
  StorageOutlined,
  TableChartOutlined,
  ThumbUpOutlined,
  TrendingUpOutlined,
  UploadFileOutlined,
} from '@mui/icons-material'
import { Box, Stack, ThemeProvider, Typography } from '@mui/material'
import { ReactNode, useLayoutEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import ExcelNavigationSvg from 'src/assets/icons/excel_navigation.svg?react'
import LabelCheckNavigationSvg from 'src/assets/icons/label_check_navigation.svg?react'
import { localStorageKeys } from 'src/constants/localStorageKeys'
import { useAuthProcess } from 'src/features/auth/useAuthProcess'
import { useLimitedFeatureAvailability } from 'src/features/featureAvailability/useLimitedFeatureAvailability'
import { Divider } from 'src/features/globalNavigation/components/Divider'
import { NavSection } from 'src/features/globalNavigation/components/NavSection'
import { NavItemExternalLink } from 'src/features/globalNavigation/components/navItem/NavItemExternalLink'
import { NavItemInternalLink } from 'src/features/globalNavigation/components/navItem/NavItemInternalLink'
import { NavItemSubMenuOpener } from 'src/features/globalNavigation/components/navItem/NavItemSubMenuOpener'
import { SubMenuContainer } from 'src/features/globalNavigation/components/subMenuItem/SubMenuContainer'
import { SubMenuDivider } from 'src/features/globalNavigation/components/subMenuItem/SubMenuDivider'
import { SubMenuItemClickable } from 'src/features/globalNavigation/components/subMenuItem/SubMenuItemClickable'
import { SubMenuItemInternalLink } from 'src/features/globalNavigation/components/subMenuItem/SubMenuItemInternalLink'
import * as siderMenuActions from 'src/features/globalNavigation/slice'
import { globalNavigationTheme } from 'src/features/globalNavigation/theme'
import { isLegacyCompany } from 'src/features/legacyFeatures/isLegacyCompany'
import { RootState } from 'src/state/store'
import { appEnv } from 'src/util/appEnv'
import { useQueryLoginUserContext } from '../tanStackQuery/commonQueries/useQueryLoginUserContext'
import { SubMenuItem } from './components/subMenuItem/SubMenuItem'
import { mapContractServicesToServiceNames } from './contractServiceMapper'

/**
 * グローバルナビゲーションにおいて各アイテムの表示・非表示を管理するための型。
 * 各アイテムの表示・非表示は、契約プランやユーザーロールによって動的に切り替わる。
 */
type ConditionalContent = {
  /**
   * 表示するコンテンツのReactノード
   */
  content: ReactNode
  /**
   * このアイテムがユーザーに対して表示されるべきかどうか
   */
  visible: boolean
}

type Props = {
  children?: ReactNode
  /**
   * このAPIは責務分離の点で問題を抱えているため、近日中に廃止予定です。
   * 新規の利用はしないでください。
   */
  resetPagination?: () => void
}

export const GlobalNavigation = (props: Props) => {
  const { children, resetPagination } = props

  const dispatch = useDispatch()

  const isCollapsed = useSelector(
    (state: RootState) => state.globalNavigationState.isCollapsed,
  )

  const { isFeatureAvailable } = useLimitedFeatureAvailability()

  // @legacy-feature: 表形式、スクロール2列、紙芝居廃止
  const isLegacy = isLegacyCompany()

  const { cleanupAndPushOutUser } = useAuthProcess()

  const toggleCollapse = () => {
    dispatch(siderMenuActions.toggle())
  }

  const userName = localStorage.getItem(localStorageKeys.loginUserName) || ''

  const FADE_PX = 40

  const loginCompanyName =
    localStorage.getItem(localStorageKeys.loginCompanyName) || ''

  // タブレット横向きよりも画面幅が狭い場合はデフォルトでサイドバーを閉じておく。
  // 参考: https://qiita.com/tomohisaota/items/f8857d01f328e34fb551
  useLayoutEffect(() => {
    if (window.innerWidth < 1024) {
      dispatch(siderMenuActions.close())
    }
  }, [dispatch])

  const mainSectionItems: ConditionalContent[] = [
    {
      content: (
        <NavItemInternalLink
          key="dashboards"
          icon={
            <SpeedOutlined
              fontSize="small"
              sx={{ width: '20px', height: '20px' }}
            />
          }
          title="ダッシュボード"
          internalPath="/dashboards"
          isCollapsed={isCollapsed}
          onClick={resetPagination}
        />
      ),
      visible: isFeatureAvailable('dashboard'),
    },
    {
      // ダッシュボード(新) アルファ版・検証用。検証終了時は丸ごと削除する。
      content: (
        <NavItemInternalLink
          key="monitoringDashboardAlpha"
          icon={
            <SpeedOutlined
              fontSize="small"
              sx={{ width: '20px', height: '20px' }}
            />
          }
          title="ダッシュボード（新）"
          isBeta
          internalPath="/monitoringDashboardAlpha"
          isCollapsed={isCollapsed}
          onClick={resetPagination}
        />
      ),
      visible: isFeatureAvailable('monitoringDashboardAlpha'),
    },
    {
      content: (
        <NavItemInternalLink
          key="scheduledReportSummary"
          icon={
            <TrendingUpOutlined
              fontSize="small"
              sx={{ width: '20px', height: '20px' }}
            />
          }
          title="実施状況"
          internalPath="/scheduledReportSummary"
          isCollapsed={isCollapsed}
          onClick={resetPagination}
        />
      ),
      visible: isFeatureAvailable('scheduledReportSummary'),
    },
    {
      content: (
        <NavItemInternalLink
          key="analytics"
          icon={
            <BarChartOutlined
              fontSize="small"
              sx={{ width: '20px', height: '20px' }}
            />
          }
          title="分析"
          isBeta
          internalPath="/analytics"
          isCollapsed={isCollapsed}
        />
      ),
      visible: isFeatureAvailable('analytics'),
    },
    {
      content: (
        <NavItemInternalLink
          key="shipmentReadinessOverview"
          icon={
            <BarChartOutlined
              fontSize="small"
              sx={{ width: '20px', height: '20px' }}
            />
          }
          title="出荷判定"
          isBeta
          internalPath="/shipmentReadinessOverview"
          isCollapsed={isCollapsed}
        />
      ),
      visible: isFeatureAvailable('shipmentReadinessOverview'),
    },
    {
      content: (
        <NavItemInternalLink
          key="reports"
          icon={
            <AssignmentOutlined
              fontSize="small"
              sx={{ width: '20px', height: '20px' }}
            />
          }
          title="レポート"
          internalPath="/reports"
          isCollapsed={isCollapsed}
        />
      ),
      visible: isFeatureAvailable('reports'),
    },
    {
      content: (
        <NavItemInternalLink
          key="approvals"
          icon={
            <ThumbUpOutlined
              fontSize="small"
              sx={{ width: '20px', height: '20px' }}
            />
          }
          title="承認"
          internalPath="/approvals"
          isCollapsed={isCollapsed}
        />
      ),
      visible: isFeatureAvailable('approvals'),
    },
    {
      content: (
        <NavItemInternalLink
          key="improves"
          icon={
            <AnalyticsOutlined
              fontSize="small"
              sx={{ width: '20px', height: '20px' }}
            />
          }
          title="カイゼン"
          internalPath="/improves"
          isCollapsed={isCollapsed}
          onClick={resetPagination}
        />
      ),
      visible: isFeatureAvailable('improves'),
    },
  ]

  const recordSectionItems: ConditionalContent[] = [
    {
      content: (
        <NavItemInternalLink
          key="templates"
          icon={
            <TableChartOutlined
              fontSize="small"
              sx={{ width: '20px', height: '20px' }}
            />
          }
          title="ひな形"
          internalPath="/templates"
          isCollapsed={isCollapsed}
          onClick={resetPagination}
        />
      ),
      visible: isFeatureAvailable('templates'),
    },
    {
      content: (
        <NavItemInternalLink
          key="schedules"
          icon={
            <CalendarMonthOutlined
              fontSize="small"
              sx={{ width: '20px', height: '20px' }}
            />
          }
          title="スケジュール"
          internalPath="/schedules"
          isCollapsed={isCollapsed}
          onClick={resetPagination}
        />
      ),
      visible: isFeatureAvailable('schedules'),
    },
    {
      content: (
        <NavItemInternalLink
          key="taskSchedules"
          icon={
            <EventRepeatOutlined
              fontSize="small"
              sx={{ width: '20px', height: '20px' }}
            />
          }
          title="作業指示（仮）"
          internalPath="/taskSchedules/add"
          isCollapsed={isCollapsed}
          onClick={resetPagination}
        />
      ),
      visible: isFeatureAvailable('taskSchedules'),
    },
    {
      content: (
        <NavItemInternalLink
          key="gridVariables"
          icon={
            <DatasetOutlined
              fontSize="small"
              sx={{ width: '20px', height: '20px' }}
            />
          }
          title="取り込み項目"
          internalPath="/gridVariables"
          isCollapsed={isCollapsed}
          onClick={resetPagination}
        />
      ),
      visible: isLegacy && isFeatureAvailable('dynamicSheet'),
    },
    {
      content: (
        <NavItemInternalLink
          key="masters"
          icon={
            <StorageOutlined
              fontSize="small"
              sx={{ width: '20px', height: '20px' }}
            />
          }
          title="製品マスタ"
          internalPath="/masters"
          isCollapsed={isCollapsed}
          onClick={resetPagination}
        />
      ),
      visible: isFeatureAvailable('masters'),
    },
    {
      content: (
        <NavItemInternalLink
          key="labelCheck"
          icon={<LabelCheckNavigationSvg />}
          title="AIラベル検査"
          internalPath="/labelMasters"
          isCollapsed={isCollapsed}
          onClick={resetPagination}
        />
      ),
      visible: isFeatureAvailable('labelCheck'),
    },
  ]
  const hasSomeRecordSectionItems = recordSectionItems.some(
    item => item.visible,
  )

  const outputSectionItems: ConditionalContent[] = [
    {
      content: (
        <NavItemInternalLink
          key="excelConversionFlows"
          icon={<ExcelNavigationSvg />}
          title="Excel変換"
          internalPath="/excelConversionFlows"
          isCollapsed={isCollapsed}
          onClick={resetPagination}
        />
      ),
      visible: isFeatureAvailable('excelConversionRead'),
    },
    {
      content: (
        <NavItemInternalLink
          key="reportExports"
          icon={
            <UploadFileOutlined
              fontSize="small"
              sx={{ width: '20px', height: '20px' }}
            />
          }
          title="レポート出力"
          internalPath="/reportExports"
          isCollapsed={isCollapsed}
          onClick={resetPagination}
        />
      ),
      visible: isFeatureAvailable('reportExports'),
    },
  ]
  const hasSomeOutputSectionItems = outputSectionItems.some(
    item => item.visible,
  )

  // 関数で定義している理由は、サブメニューを閉じる機能を外から注入できるようにするため
  const accountSubMenuItems = (
    closeSubMenu: () => void,
  ): ConditionalContent[] => [
    {
      content: (
        <SubMenuItem
          key="userId"
          caption="ユーザー管理番号"
          title={`${localStorage.getItem(localStorageKeys.loginUserRumpCustomerMemberId) || ''}`}
        />
      ),
      visible: true,
    },
    {
      content: <SubMenuDivider key="subMenuDivider" />,
      visible: true, // 型を分けるのが煩雑なので便宜上書いただけで意味はない
    },
    {
      content: (
        <SubMenuItemClickable
          icon={
            <Logout
              fontSize="small"
              sx={{ width: '20px', height: '20px', marginRight: '4px' }}
            />
          }
          key="logout"
          title="ログアウト"
          onClick={() => {
            cleanupAndPushOutUser({ serverRequest: true })
            closeSubMenu()
          }}
        />
      ),
      visible: true,
    },
  ]

  // 関数で定義している理由は、サブメニューを閉じる機能を外から注入できるようにするため
  const settingSubMenuItems = (
    closeSubMenu: () => void,
  ): ConditionalContent[] => [
    {
      content: (
        <SubMenuItemInternalLink
          key="places"
          internalPath="/places"
          onClick={() => {
            resetPagination?.()
            closeSubMenu()
          }}
          title="現場"
        />
      ),
      visible: isFeatureAvailable('places'),
    },
    {
      content: (
        <SubMenuItemInternalLink
          key="placeGroups"
          internalPath="/placeGroups"
          onClick={() => {
            resetPagination?.()
            closeSubMenu()
          }}
          title="現場グループ"
        />
      ),
      visible: isFeatureAvailable('places'),
    },
    {
      content: 'divider',
      visible: true, // 型を分けるのが煩雑なので便宜上書いただけで意味はない
    },
    {
      content: (
        <SubMenuItemInternalLink
          key="users"
          internalPath="/users"
          onClick={() => {
            resetPagination?.()
            closeSubMenu()
          }}
          title="ユーザー"
        />
      ),
      visible: isFeatureAvailable('users'),
    },
    {
      content: (
        <SubMenuItemInternalLink
          key="userGroups"
          internalPath="/userGroups"
          onClick={() => {
            resetPagination?.()
            closeSubMenu()
          }}
          title="ユーザーグループ"
        />
      ),
      visible: isFeatureAvailable('users'),
    },
    {
      content: (
        <SubMenuItemInternalLink
          key="employees"
          internalPath="/employees"
          onClick={() => {
            resetPagination?.()
            closeSubMenu()
          }}
          title="従業員"
        />
      ),
      visible: isFeatureAvailable('employees'),
    },
    {
      content: (
        <SubMenuItemInternalLink
          key="devices"
          internalPath="/devices"
          onClick={() => {
            resetPagination?.()
            closeSubMenu()
          }}
          title="キオスク端末"
        />
      ),
      visible: isFeatureAvailable('devices'),
    },
    {
      content: 'divider',
      visible: true, // 型を分けるのが煩雑なので便宜上書いただけで意味はない
    },
    {
      content: (
        <SubMenuItemInternalLink
          key="glossaries"
          internalPath="/glossaries"
          onClick={() => {
            resetPagination?.()
            closeSubMenu()
          }}
          title="翻訳用語集設定"
        />
      ),
      visible: isFeatureAvailable('multiLanguageSupport'),
    },
    {
      content: (
        <SubMenuItemInternalLink
          key="companySettings"
          internalPath="/companySettings"
          onClick={() => {
            resetPagination?.()
            closeSubMenu()
          }}
          title="アプリ設定"
        />
      ),
      visible: isFeatureAvailable('companySettings'),
    },
    {
      content: (
        <SubMenuItemInternalLink
          key="optionUsages"
          internalPath="/optionUsages"
          onClick={() => {
            resetPagination?.()
            closeSubMenu()
          }}
          title="AIラベル検査使用量"
        />
      ),
      visible: isFeatureAvailable('labelCheck'),
    },
  ]
  const hasSomeSettingSubMenuItems = settingSubMenuItems(() => null).some(
    item => item.content !== 'divider' && item.visible,
  )

  const { data: loginUserContext } = useQueryLoginUserContext()

  return (
    <div css={styles.container}>
      <div css={[styles.navColumn, isCollapsed && styles.navColumnCollapsed]}>
        <Stack sx={{ height: '100vh' }} pt={2}>
          {isCollapsed ? (
            <Box
              onClick={toggleCollapse}
              sx={{
                height: '48px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                ':hover': {
                  color: `${globalNavigationTheme.textColor}`,
                  background: `${globalNavigationTheme.backgroundHover}`,
                },
              }}
            >
              <MenuOutlined
                fontSize="small"
                sx={{ width: '20px', height: '20px' }}
              />
            </Box>
          ) : (
            <ThemeProvider theme={launcherTheme}>
              <Launcher
                embeddedIn="report"
                contractServices={
                  loginUserContext?.contractServices
                    ? mapContractServicesToServiceNames(
                        loginUserContext.contractServices,
                      )
                    : []
                }
                env={appEnv.isProduction ? 'production' : 'development'}
                onLogoClick={toggleCollapse}
              />
            </ThemeProvider>
          )}
          <Stack
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              WebkitMaskImage: `linear-gradient(
                to bottom,
                black 0,
                black calc(100% - ${FADE_PX}px),
                transparent 100%
              )`,
              maskImage: `linear-gradient(
                to bottom,
                black 0,
                black calc(100% - ${FADE_PX}px),
                transparent 100%
              )`,
            }}
          >
            {/* メインセクション */}
            <Stack spacing={1} mt={2}>
              {mainSectionItems.map(item => item.visible && item.content)}
            </Stack>

            {/* 記録設定セクション */}
            {hasSomeRecordSectionItems && (
              <Stack spacing={1} mt={2}>
                {!isCollapsed && <NavSection title="記録設定" />}
                {recordSectionItems.map(item => item.visible && item.content)}
              </Stack>
            )}

            {/* 出力セクション */}
            {hasSomeOutputSectionItems && (
              <Stack spacing={1} mt={2}>
                {!isCollapsed && <NavSection title="出力" />}
                {outputSectionItems.map(item => item.visible && item.content)}
              </Stack>
            )}
            <Stack spacing={1} mt={2} sx={{ marginBottom: `${FADE_PX}px` }}>
              {hasSomeSettingSubMenuItems && !isCollapsed && (
                <NavSection title="その他の設定" />
              )}
              {/* 設定サブメニュー */}
              {hasSomeSettingSubMenuItems && (
                <NavItemSubMenuOpener
                  icon={
                    <SettingsOutlined
                      fontSize="small"
                      sx={{ width: '20px', height: '20px' }}
                    />
                  }
                  renderSubmenu={closeSubMenu => (
                    <SubMenuContainer>
                      {settingSubMenuItems(closeSubMenu).map(
                        (item, index, arr) => {
                          if (item.content !== 'divider') {
                            return item.visible && item.content
                          }

                          // 以下、区切り線を描写するためのコード。
                          // サブメニューの項目はユーザー属性によって表示・非表示がダイナミックに変わるので、
                          // このような手当てをしないとDividerが重複したり不要な場所に描写されたりする。
                          const previousItem = arr[index - 1]
                          const nextItem = arr[index + 1]
                          if (
                            // 前後に項目がある場合のみ区切り線を表示する
                            previousItem &&
                            previousItem.content !== 'divider' &&
                            previousItem.visible &&
                            nextItem &&
                            nextItem.content !== 'divider' &&
                            nextItem.visible
                          ) {
                            return <SubMenuDivider key={`divider-${index}`} />
                          }
                          return null
                        },
                      )}
                    </SubMenuContainer>
                  )}
                  title="設定"
                  aria-label="設定"
                  role="button"
                  isCollapsed={isCollapsed}
                />
              )}
            </Stack>
          </Stack>

          <Stack sx={{ flexShrink: 0 }}>
            <Divider isCollapsed={isCollapsed} />

            <Stack spacing={1} mt={2}>
              <NavItemExternalLink
                title="ヘルプページ"
                isCollapsed={isCollapsed}
                externalPath="https://docs.channel.io/kaminashireport?utm_source=admin&utm_medium=menu"
              />

              {/* アカウントサブメニュー */}
              <NavItemSubMenuOpener
                icon={
                  isCollapsed && (
                    <PersonOutline
                      fontSize="small"
                      sx={{ width: '20px', height: '20px' }}
                    />
                  )
                }
                renderSubmenu={closeSubMenu => (
                  <SubMenuContainer>
                    {accountSubMenuItems(closeSubMenu).map(
                      item => item.visible && item.content,
                    )}
                  </SubMenuContainer>
                )}
                title={userName}
                role="button"
                aria-label={userName}
                isCollapsed={isCollapsed}
                placement={['right-end']}
              />
              {!isCollapsed && loginCompanyName && (
                <Typography variant="caption" sx={{ px: 3 }}>
                  {loginCompanyName}
                </Typography>
              )}
            </Stack>

            <div css={styles.spacerForChannelTalk} />
          </Stack>
        </Stack>
      </div>
      <div css={styles.mainContentColumn}>{children}</div>
    </div>
  )
}

const styles = {
  container: css`
    display: flex;
  `,
  navColumn: css`
    background: ${globalNavigationTheme.background};
    color: ${globalNavigationTheme.textColor};
    flex: 0 0 200px;
    height: 100vh;
    overflow-x: hidden;
    overscroll-behavior: none;
    position: sticky;
    top: 0;
    scrollbar-width: thin;
  `,
  navColumnCollapsed: css`
    flex-basis: 40px;
  `,
  sorter: css`
    display: grid;
    gap: 4px;
  `,
  outputSection: css`
    margin-top: 16px;
  `,
  spacerForChannelTalk: css`
    height: 100px;
    flex: 0 0 auto;
  `,
  mainContentColumn: css`
    flex: 1 1 auto;
    overflow: hidden;
  `,
}
